import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Formik, Form } from 'formik';

import {
  SELF_SHOWING_ISSUE_TYPE_INCORRECT_CODE,
  SELF_SHOWING_ISSUE_TYPE_DOOR_LOCKED,
  SELF_SHOWING_ISSUE_TYPE_KEY_NOT_IN_LOCKBOX,
  SELF_SHOWING_ISSUE_TYPE_LOCKBOX_NOT_WORKING,
  SELF_SHOWING_ISSUE_TYPE_OTHER,
} from 'common/selfShowingLockConstants';

import { initialApiActionState } from 'api/createApiClient';
import { SR_API_ACTION_SELF_SHOWING_LOCK_REPORT_ERROR } from 'api/actionTypes';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrDarkModal from 'client/components/SrDarkModal';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import SrFormikField, { SR_FIELD_THEME_PARTNER_FORM } from 'client/components/SrFormikField';

import postWithState from 'client/actions/postWithState';
import useBranch from 'client/hooks/useBranch';

const formFields = [
  {
    id: 'issueType',
    type: 'multiple_choice',
    validations: ['required'],
    properties: {
      multipleSelection: false,
      useArrayValues: false,
      toggleSingleSelection: true,
      uncheckedValue: null,
      choices: [
        {
          label: 'Incorrect code',
          value: SELF_SHOWING_ISSUE_TYPE_INCORRECT_CODE,
        },
        {
          label: 'Door locked',
          value: SELF_SHOWING_ISSUE_TYPE_DOOR_LOCKED,
        },
        {
          label: 'Key not in lockbox',
          value: SELF_SHOWING_ISSUE_TYPE_KEY_NOT_IN_LOCKBOX,
        },
        {
          label: 'Lockbox not working',
          value: SELF_SHOWING_ISSUE_TYPE_LOCKBOX_NOT_WORKING,
        },
        {
          label: 'Other',
          value: SELF_SHOWING_ISSUE_TYPE_OTHER,
        },
      ],
    },
  },
  {
    id: 'issue',
    type: 'long_text',
    validations: ['required'],
    properties: {
      placeholder: 'Write the reason for not entering the home here...',
    },
  },
];

const SrSelfShowingCodeboxIssueForm = ({
  userId,
  listing,
  onSubmit,
  onClose,
  dispatch,
  clientName,
  clientPhone,
  clientEmail,
}) => {
  const { apiStateReport = initialApiActionState } = useBranch({
    apiStateReport: [
      'apiState',
      SR_API_ACTION_SELF_SHOWING_LOCK_REPORT_ERROR,
      'selfShowingLockReportIssueApiState',
    ],
  });

  const _onSubmit = (formValues) => {
    dispatch(postWithState, {
      action: SR_API_ACTION_SELF_SHOWING_LOCK_REPORT_ERROR,
      queryId: 'selfShowingLockReportIssueApiState',
      payload: {
        userId: userId,
        listingId: listing.id,
        clientName: clientName,
        clientPhone: clientPhone,
        clientEmail: clientEmail,
        accessCodeShown: true,
        doorHandleLocked: null,
        issue: formValues.issue,
        issueType: formValues.issueType,
      },
      onSuccess: () => {
        onSubmit();
      },
    });
  };

  return (
    <SrDarkModal>
      <div
        className={classnames('SrListingSelfTouringSection sunlockStatusModal sunlockLockForm')}
        onClick={(event) => event.stopPropagation()}
      >
        <SrFlex marginBottom="16px" justify="center">
          <SrText
            color="blueGreycliff"
            font="greycliff"
            weight="800"
            fontSize="21px"
            lineHeight="30px"
            align="center"
          >
            Why can’t you enter?
          </SrText>
        </SrFlex>
        <SrFlex direction="col">
          <Formik
            validate={(formValues) => {
              const errors = createFormValidationFn(formFields)(formValues);
              return errors;
            }}
            enableReinitialize
            validateOnChange
            initialValues={createInitialValues({ formFields })}
            onSubmit={_onSubmit}
            render={(formikBag) => {
              return (
                <Form autoComplete="off" style={{ height: '100%' }}>
                  <SrFlex direction="col" justify="between" height="100%">
                    <SrBox>
                      {formFields.map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          formField={formField}
                          formikBag={formikBag}
                          className={formField.classnames || SR_FIELD_THEME_PARTNER_FORM}
                        />
                      ))}
                    </SrBox>
                    <SrBox>
                      <SrButton
                        type="submit"
                        theme={SR_BUTTON_THEME_ADOBE_GREEN}
                        disabled={apiStateReport.inProgress}
                      >
                        {apiStateReport.inProgress ? 'Please wait...' : 'Submit'}
                      </SrButton>

                      <SrBox paddingTop="16px">
                        <SrButton
                          type="button"
                          onClick={onClose}
                          theme={SR_BUTTON_THEME_ADOBE_GREY}
                          disabled={apiStateReport.inProgress}
                        >
                          Back
                        </SrButton>
                      </SrBox>
                    </SrBox>
                  </SrFlex>
                </Form>
              );
            }}
          />
        </SrFlex>
      </div>
    </SrDarkModal>
  );
};

SrSelfShowingCodeboxIssueForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  listing: PropTypes.object.isRequired,
  userId: PropTypes.number.isRequired,
  dispatch: PropTypes.func.isRequired,
  clientName: PropTypes.string,
  clientPhone: PropTypes.string,
  clientEmail: PropTypes.string,
};

SrSelfShowingCodeboxIssueForm.displayName = 'SrSelfShowingCodeboxIssueForm';

export default SrSelfShowingCodeboxIssueForm;
